import React, { useState, useEffect, useCallback } from "react";
import PortfolioItem from "./PortfolioItem";
import { FaSpinner } from "react-icons/fa";
import { fetchPortfolio, fetchPortfolioCategories } from "../../utils/portfolioApi";

const PortfolioFive = ({ Column = "col-lg-4 col-md-6 col-sm-6 col-12 mt--40" }) => {
    const [allItems, setAllItems] = useState([]);
    const [visibleItems, setVisibleItems] = useState([]);
    const [visibleCount, setVisibleCount] = useState(6);
    const [activeFilter, setActiveFilter] = useState("all");
    const [filters, setFilters] = useState([{ id: 1, text: "All" }]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [noMoreItems, setNoMoreItems] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const increment = 6;

    // Load data once
    useEffect(() => {
        let cancelled = false;

        const loadData = async () => {
            setLoading(true);
            setError(null);

            try {
                const [items, categories] = await Promise.all([
                    fetchPortfolio({ limit: 200 }),
                    fetchPortfolioCategories()
                ]);

                if (cancelled) return;

                const safeItems = Array.isArray(items) ? items : [];
                setAllItems(safeItems);

                // Build dynamic filters
                const dynamicFilters = [
                    { id: 1, text: "All" },
                    ...(Array.isArray(categories) ? categories : [])
                        .filter(c => c?.category?.trim())
                        .map((c, i) => ({ id: i + 2, text: c.category.trim() }))
                ];

                setFilters(dynamicFilters);
                setActiveFilter("all");
                applyFilter("all", safeItems, visibleCount);
            } catch (err) {
                if (!cancelled) {
                    console.error("Failed to load portfolio:", err);
                    setError("Failed to load portfolio items. Is the backend running?");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadData();
        return () => { cancelled = true; };
    }, []);

    // Apply filter + pagination
    const applyFilter = useCallback((filterKey, items = allItems, count = visibleCount) => {
        let filtered = items;

        if (filterKey !== "all") {
            filtered = items.filter(item =>
                item.category?.toLowerCase() === filterKey.toLowerCase()
            );
        }

        const sliced = filtered.slice(0, count);
        setVisibleItems(sliced);
        setNoMoreItems(count >= filtered.length);
    }, [allItems, visibleCount]);

    // Handle filter change
    const handleFilterChange = (filterText) => {
        const key = filterText.toLowerCase();
        setActiveFilter(key);
        setVisibleCount(increment);
        setNoMoreItems(false);
        applyFilter(key, allItems, increment);
        setIsDropdownOpen(false);
    };

    // Load more items
    const handleLoadMore = () => {
        const newCount = visibleCount + increment;
        setVisibleCount(newCount);
        applyFilter(activeFilter, allItems, newCount);
    };

    // Close dropdown on outside click
    useEffect(() => {
        if (!isDropdownOpen) return;

        const close = (e) => {
            if (!e.target.closest(".portfolio-filter-dropdown")) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [isDropdownOpen]);

    return (
        <>
            {/* Filter Buttons */}
            <div className="row row--15">
                <div className="col-lg-12">
                    {/* Desktop Filters */}
                    <ul className="rwt-portfolio-filter filter-button-default liststyle mb--20 d-none d-lg-flex">
                        {filters.map(filter => (
                            <li key={filter.id}>
                                <button
                                    onClick={() => handleFilterChange(filter.text)}
                                    className={activeFilter === filter.text.toLowerCase() ? "current" : ""}
                                >
                                    {filter.text}
                                    {filter.count !== undefined && (
                                        <span className="ms-2 text-muted">({filter.count})</span>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* Mobile Dropdown */}
                    <div className="portfolio-filter-dropdown mb--20 d-lg-none">
                        <button
                            className="filter-toggle btn btn-outline-primary w-100 text-start d-flex justify-content-between align-items-center"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            aria-expanded={isDropdownOpen}
                        >
                            <span>
                                {filters.find(f => f.text.toLowerCase() === activeFilter)?.text || "All"}
                            </span>
                            <span>{isDropdownOpen ? "Up" : "Down"}</span>
                        </button>

                        {isDropdownOpen && (
                            <ul className="filter-menu list-unstyled mt-2 border rounded shadow-sm bg-white">
                                {filters.map(filter => (
                                    <li key={filter.id}>
                                        <button
                                            className={`w-100 text-start px-4 py-2 ${activeFilter === filter.text.toLowerCase() ? "bg-primary text-white" : "hover-bg-light"}`}
                                            onClick={() => handleFilterChange(filter.text)}
                                        >
                                            {filter.text}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Portfolio Grid */}
            <div className="row row--15">
                {loading ? (
                    <div className="col-12 text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading portfolio items...</p>
                    </div>
                ) : error ? (
                    <div className="col-12 text-center py-5">
                        <div className="alert alert-danger">
                            <strong>Error:</strong> {error}
                        </div>
                        <small className="text-muted">
                            Make sure your Next.js API is running on port 3001
                        </small>
                    </div>
                ) : visibleItems.length === 0 ? (
                    <div className="col-12 text-center py-5">
                        <p className="text-muted fs-5">No portfolio items found.</p>
                        {activeFilter !== "all" && (
                            <button
                                className="btn btn-link"
                                onClick={() => handleFilterChange("All")}
                            >
                                ← Show all projects
                            </button>
                        )}
                    </div>
                ) : (
                    visibleItems.map((item) => (
                        <div key={item.id || item.slug} className={Column}>
                            <PortfolioItem portfolio={item} />
                        </div>
                    ))
                )}
            </div>

            {/* Load More Button */}
            {!loading && !error && visibleItems.length > 0 && (
                <div className="row mt--50">
                    <div className="col-lg-12 text-center">
                        <button
                            className="btn btn-primary btn-large"
                            onClick={handleLoadMore}
                            disabled={noMoreItems}
                        >
                            {noMoreItems ? (
                                "No More Items"
                            ) : (
                                <>
                                    Load More <FaSpinner className="ms-2" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default PortfolioFive;